import type { StrategyDocument, StrategyPatchOp } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { getAtPath, opKey } from "@/lib/strategyDraft";
import {
  BoolField,
  FieldRow,
  NumberField,
  SelectField,
  TextField,
  TextListField,
} from "@/components/strategy/FieldEditors";

/**
 * The strategy-level configuration as editable cards (2.8): what applies to
 * every phase — identity, sending rules, strategy-wide safeguards, knowledge
 * retrieval — with each field wired to a patch op. Structures the form does
 * not model (prompt template overrides, prospect schema, per-domain limits)
 * are summarized read-only and edited in the YAML editor.
 */
export default function StrategyOverviewCards({
  document,
  pendingOps,
  onEdit,
}: {
  document: StrategyDocument;
  pendingOps: StrategyPatchOp[];
  onEdit: (op: StrategyPatchOp) => void;
}) {
  const read = (keyPath: string[]) => getAtPath(document, "strategy", null, keyPath);
  const edit = (keyPath: string[], value: unknown) =>
    onEdit({ scope: "strategy", phase: null, keyPath, value: value ?? null });
  const isEdited = (keyPath: string[]) =>
    pendingOps.some(
      (o) => opKey(o) === opKey({ scope: "strategy", phase: null, keyPath, value: null }),
    );

  const str = (p: string[]) => {
    const v = read(p);
    return typeof v === "string" ? v : null;
  };
  const num = (p: string[]) => {
    const v = read(p);
    return typeof v === "number" ? v : null;
  };
  const list = (p: string[]) => {
    const v = read(p);
    return Array.isArray(v) ? (v as string[]) : null;
  };

  const prompts = read(["prompts"]) as Record<string, unknown> | undefined;
  const promptOverrides = prompts && typeof prompts === "object"
    ? Object.keys(prompts)
    : [];
  const schemaFields = read(["prospect_schema", "fields"]);
  const schemaCount = Array.isArray(schemaFields) ? schemaFields.length : 0;

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <Card>
        <CardTitle>General</CardTitle>
        <div className="mt-2 space-y-3">
          <FieldRow label="Name" edited={isEdited(["name"])}>
            <TextField ariaLabel="Strategy name" value={str(["name"])}
              onCommit={(v) => edit(["name"], v)} />
          </FieldRow>
          <FieldRow label="Description" edited={isEdited(["description"])}>
            <TextField ariaLabel="Strategy description" multiline value={str(["description"])}
              onCommit={(v) => edit(["description"], v)} />
          </FieldRow>
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="Version" edited={isEdited(["version"])} hint="Your own label — nothing branches on it">
              <TextField ariaLabel="Strategy version" value={str(["version"])}
                onCommit={(v) => edit(["version"], v)} />
            </FieldRow>
            <FieldRow
              label="Active"
              edited={isEdited(["active"])}
              hint="Off stops new conversations starting and inbound mail being polled. Prospects already running keep going."
            >
              <BoolField label="strategy is live" value={read(["active"]) === true}
                onCommit={(v) => edit(["active"], v)} />
            </FieldRow>
          </div>
        </div>
      </Card>

      <Card>
        <CardTitle>Safeguards (strategy-wide)</CardTitle>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          Non-negotiable rules appended to <em>every</em> AI prompt for this strategy —
          all phases, all call sites. Prompt overrides cannot remove them. Phases can add
          their own on top.
        </p>
        <div className="mt-2">
          <FieldRow label="Rules" edited={isEdited(["safeguards"])}>
            <TextListField
              addLabel="Add safeguard, e.g. “Never make a binding offer.”"
              value={list(["safeguards"])}
              onCommit={(v) => edit(["safeguards"], v)}
            />
          </FieldRow>
        </div>
      </Card>

      <Card>
        <CardTitle>Email &amp; sending</CardTitle>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          The same values as Settings → Runtime for this strategy; either screen changes
          the other. The send window and limits apply to every prospect on this strategy.
        </p>
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="Sender name" edited={isEdited(["email", "sender_name"])}>
              <TextField ariaLabel="Sender name" value={str(["email", "sender_name"])}
                onCommit={(v) => edit(["email", "sender_name"], v)} />
            </FieldRow>
            <FieldRow
              label="Approval mode"
              edited={isEdited(["email", "approval_mode"])}
              hint="review = a human approves every email"
            >
              <SelectField
                ariaLabel="Approval mode"
                value={str(["email", "approval_mode"])}
                options={[
                  { value: "review", label: "review" },
                  { value: "auto", label: "auto" },
                ]}
                allowEmpty="(default: review)"
                onCommit={(v) => edit(["email", "approval_mode"], v)}
              />
            </FieldRow>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <FieldRow
              label="Max / day"
              edited={isEdited(["email", "max_emails_per_day"])}
              hint="Whole strategy, all prospects together — not per prospect"
            >
              <NumberField ariaLabel="Max emails per day" min={1}
                value={num(["email", "max_emails_per_day"])}
                onCommit={(v) => edit(["email", "max_emails_per_day"], v)} />
            </FieldRow>
            <FieldRow
              label="Min delay (h)"
              edited={isEdited(["email", "min_delay_between_emails_hours"])}
              hint="Quiet period per prospect between two messages to that same person"
            >
              <NumberField ariaLabel="Minimum delay between emails in hours" min={0}
                value={num(["email", "min_delay_between_emails_hours"])}
                onCommit={(v) => edit(["email", "min_delay_between_emails_hours"], v)} />
            </FieldRow>
            <FieldRow
              label="Max / domain / day"
              edited={isEdited(["email", "max_emails_per_domain_per_day"])}
              hint="Ceiling per recipient domain, so one organisation never takes the whole day"
            >
              <NumberField ariaLabel="Max emails per domain per day" min={1}
                value={num(["email", "max_emails_per_domain_per_day"])}
                onCommit={(v) => edit(["email", "max_emails_per_domain_per_day"], v)} />
            </FieldRow>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <FieldRow
              label="Send from (hour)"
              edited={isEdited(["email", "send_window", "start_hour"])}
              hint="Inclusive — 9 = from 09:00"
            >
              <NumberField ariaLabel="Send window start hour" min={0} max={23}
                value={num(["email", "send_window", "start_hour"])}
                onCommit={(v) => edit(["email", "send_window", "start_hour"], v)} />
            </FieldRow>
            <FieldRow
              label="Until (hour)"
              edited={isEdited(["email", "send_window", "end_hour"])}
              hint="Exclusive — 17 = last send 16:59; 24 = end of day"
            >
              <NumberField ariaLabel="Send window end hour" min={1} max={24}
                value={num(["email", "send_window", "end_hour"])}
                onCommit={(v) => edit(["email", "send_window", "end_hour"], v)} />
            </FieldRow>
            <FieldRow
              label="Timezone"
              edited={isEdited(["email", "send_window", "timezone"])}
              hint="Zone for the window and the daily cap's reset; a prospect's own timezone wins for the window"
            >
              <TextField ariaLabel="Send window timezone" value={str(["email", "send_window", "timezone"])}
                placeholder="e.g. Europe/Amsterdam"
                onCommit={(v) => edit(["email", "send_window", "timezone"], v)} />
            </FieldRow>
          </div>
          <FieldRow
            label="New conversations / day"
            edited={isEdited(["email", "start_pacing", "max_new_prospects_per_day"])}
            hint="0 or empty = only manual starts"
          >
            <NumberField ariaLabel="Max new prospects per day" min={0}
              value={num(["email", "start_pacing", "max_new_prospects_per_day"])}
              onCommit={(v) => edit(["email", "start_pacing", "max_new_prospects_per_day"], v)} />
          </FieldRow>
        </div>
      </Card>

      <Card>
        <CardTitle>Knowledge retrieval</CardTitle>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <FieldRow
            label="Passages per prompt"
            edited={isEdited(["knowledge_base", "rag_top_k"])}
            hint="How many knowledge chunks are retrieved per email; more context, more tokens"
          >
            <NumberField ariaLabel="RAG top K" min={1}
              value={num(["knowledge_base", "rag_top_k"])}
              onCommit={(v) => edit(["knowledge_base", "rag_top_k"], v)} />
          </FieldRow>
          <FieldRow
            label="Similarity threshold"
            edited={isEdited(["knowledge_base", "similarity_threshold"])}
            hint="0–1; higher = stricter matches"
          >
            <NumberField ariaLabel="Similarity threshold" min={0} max={1}
              value={num(["knowledge_base", "similarity_threshold"])}
              onCommit={(v) => edit(["knowledge_base", "similarity_threshold"], v)} />
          </FieldRow>
        </div>
      </Card>

      <Card>
        <CardTitle>Advanced (YAML)</CardTitle>
        <dl className="mt-2 space-y-1.5 text-xs">
          <div className="flex justify-between gap-2">
            <dt className="text-[var(--fg-muted)]">Prompt template overrides</dt>
            <dd>{promptOverrides.length > 0 ? promptOverrides.join(", ") : "none (platform defaults)"}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-[var(--fg-muted)]">Prospect form fields</dt>
            <dd className="tabular-nums">{schemaCount}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-[var(--fg-muted)]">Per-domain send limits</dt>
            <dd className="tabular-nums">
              {Object.keys((read(["email", "domain_daily_limits"]) as object) ?? {}).length}
            </dd>
          </div>
        </dl>
        <p className="mt-2 text-xs text-[var(--fg-muted)]">
          These structures are edited in the full strategy YAML at the bottom of the page.
        </p>
      </Card>
    </div>
  );
}
